import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type {
  ReleaseDetail,
  ReleaseQaStatus,
  ReleaseScopeItem,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { QA_STATUS_LABELS } from "./releaseVocabulary";

/**
 * Recording how testing went for one item (§25).
 *
 * <p>A failure asks for a comment and will not save without one. "Failed" on its own gives the
 * developer nothing to act on, and the only reason to record a failure at all is that somebody
 * has to do something about it — the server refuses it too.
 */
export function RecordQaDrawer({
  release,
  item,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  item: ReleaseScopeItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState<ReleaseQaStatus>("PASSED");
  const [comment, setComment] = useState("");
  const [defects, setDefects] = useState("0");
  const [build, setBuild] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (item) {
      setStatus(item.qaStatus ?? "PASSED");
      setComment(item.qaComment ?? "");
      setDefects(String(item.defectsFound ?? 0));
      setBuild("");
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  if (!item) {
    return null;
  }

  const needsComment = status === "FAILED";
  const canSubmit = !needsComment || comment.trim() !== "";

  const submit = () =>
    void run(
      () =>
        api.put(`/api/v1/releases/${release.id}/scope/items/${item.id}/qa`, {
          qaStatus: status,
          comment: comment.trim() === "" ? null : comment.trim(),
          defectsFound: Number(defects) || 0,
          testedBuild: build.trim() === "" ? null : build.trim(),
          environmentId: null,
          lockVersion: item.qaLockVersion,
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open
      onClose={onClose}
      title="Record QA"
      subtitle={`${item.itemKey} — ${item.title}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : "Save result"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <Field label="Result" required>
          <Select
            value={status}
            onChange={(event) => setStatus(event.target.value as ReleaseQaStatus)}
          >
            {Object.entries(QA_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={needsComment ? "What went wrong" : "Notes"}
          required={needsComment}
          hint={
            needsComment
              ? "A failure with no sentence beside it gives the developer nothing to act on."
              : "Optional."
          }
        >
          <Textarea
            rows={3}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Defects found">
            <Input
              type="number"
              min={0}
              value={defects}
              onChange={(event) => setDefects(event.target.value)}
            />
          </Field>
          <Field label="Build tested">
            <Input
              value={build}
              placeholder="build-482"
              onChange={(event) => setBuild(event.target.value)}
            />
          </Field>
        </div>
      </div>
    </Drawer>
  );
}
